import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { api, formatErr } from "@/lib/api";

export default function AdminPageContent() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draftKey, setDraftKey] = useState("");
  const [draftLabel, setDraftLabel] = useState("");

  const load = () => api.get("/admin/page-content").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draftKey.trim()) return toast.error("Clé requise (ex: home.hero)");
    try {
      await api.put(`/admin/page-content/${draftKey.trim()}`, {
        key: draftKey.trim(),
        label: draftLabel.trim() || draftKey.trim(),
        content: {},
      });
      setDraftKey(""); setDraftLabel("");
      load();
      toast.success("Bloc créé");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (key) => {
    if (!window.confirm(`Supprimer le bloc "${key}" ?`)) return;
    try { await api.delete(`/admin/page-content/${key}`); load(); toast.success("Supprimé"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <div className="font-heading text-3xl font-black uppercase tracking-tight">Contenu des pages</div>
      <div className="h-1 w-16 bg-brand mt-2 mb-2" />
      <p className="text-zinc-400 text-sm mb-6">Chaque bloc est identifié par une clé (ex : <code className="text-brand">home.hero</code>) et contient un dictionnaire libre. Les pages publiques lisent ces blocs depuis l'API.</p>

      <div className="bg-ink-900 border border-ink-700 p-4 mb-6">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Nouveau bloc</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <input className="input-admin" placeholder="Clé (home.hero, footer.about…)" value={draftKey} onChange={(e) => setDraftKey(e.target.value)} />
          <input className="input-admin sm:col-span-2" placeholder="Libellé d'affichage (optionnel)" value={draftLabel} onChange={(e) => setDraftLabel(e.target.value)} />
          <button onClick={create} className="bg-brand hover:bg-brand-600 text-white px-4 py-2.5 sm:py-0 inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-sm transition-colors"><Plus size={14} /> Créer</button>
        </div>
      </div>

      <div className="grid gap-3">
        {rows.map((r) => (
          <div key={r.key} className="bg-ink-900 border border-ink-700 p-4">
            {editing === r.key ? (
              <EditBlock row={r} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-brand font-bold">{r.key}</code>
                    {r.label && <span className="text-zinc-400 text-sm">— {r.label}</span>}
                  </div>
                  <div className="text-zinc-500 text-xs mt-1">{Object.keys(r.content || {}).length} champ(s)</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setEditing(r.key)} className="text-zinc-400 hover:text-brand text-xs font-bold uppercase">Modifier</button>
                  <button onClick={() => remove(r.key)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!rows.length && <div className="text-zinc-500 text-center py-8">Aucun bloc. Créez-en un avec une clé comme <code>home.hero</code>.</div>}
      </div>

      <style>{`.input-admin{ width:100%; height:40px; padding:0 12px; background:#3C3C40; border:1px solid #4A4A4D; color:#fff; font-size:14px; outline:none; } .input-admin:focus{ border-color:#FF6200;}`}</style>
    </div>
  );
}

function EditBlock({ row, onSaved, onCancel }) {
  const [label, setLabel] = useState(row.label || "");
  const [entries, setEntries] = useState(
    Object.entries(row.content || {}).map(([k, v]) => ({ k, v: typeof v === "string" ? v : JSON.stringify(v, null, 2) }))
  );
  const [saving, setSaving] = useState(false);

  const setKey = (i, k) => setEntries((arr) => arr.map((e, idx) => idx === i ? { ...e, k } : e));
  const setVal = (i, v) => setEntries((arr) => arr.map((e, idx) => idx === i ? { ...e, v } : e));
  const addField = () => setEntries((arr) => [...arr, { k: "", v: "" }]);
  const removeField = (i) => setEntries((arr) => arr.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const content = {};
      for (const { k, v } of entries) {
        if (!k.trim()) continue;
        // Try JSON parse if it looks like an object/array
        const trimmed = (v || "").trim();
        if ((trimmed.startsWith("[") || trimmed.startsWith("{"))) {
          try { content[k] = JSON.parse(trimmed); continue; } catch {}
        }
        content[k] = v;
      }
      await api.put(`/admin/page-content/${row.key}`, { key: row.key, label, content });
      toast.success("Bloc enregistré");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <code className="text-brand font-bold">{row.key}</code>
      </div>
      <input className="input-admin mb-3" placeholder="Libellé d'affichage" value={label} onChange={(e) => setLabel(e.target.value)} />
      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Champs</div>
      <div className="grid gap-2">
        {entries.map((e, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-start">
            <input className="input-admin col-span-12 sm:col-span-4" placeholder="Clé" value={e.k} onChange={(ev) => setKey(i, ev.target.value)} />
            <textarea className="input-admin col-span-10 sm:col-span-7 py-2" rows={Math.min(8, Math.max(1, (e.v || "").split("\n").length))} style={{ height: "auto" }} value={e.v} onChange={(ev) => setVal(i, ev.target.value)} />
            <button onClick={() => removeField(i)} className="col-span-2 sm:col-span-1 text-zinc-500 hover:text-red-400 h-10 flex items-center justify-center"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={addField} className="text-brand text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 self-start"><Plus size={12} /> Ajouter un champ</button>
      </div>
      <div className="mt-4 flex gap-2">
        <button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-600 text-white px-4 py-2 text-sm font-bold uppercase inline-flex items-center gap-2 disabled:opacity-50"><Save size={14} /> Enregistrer</button>
        <button onClick={onCancel} className="bg-ink-800 text-white px-4 py-2 text-sm font-bold uppercase">Annuler</button>
      </div>
      <p className="text-zinc-500 text-xs mt-2">Les listes/objets peuvent être saisis en JSON (commence par <code>[</code> ou <code>{`{`}</code>).</p>
    </div>
  );
}
